import {
    ADMIN_GOOGLE_REQUIRED_MESSAGE,
    UserRole,
    isAdminEmail,
    isFirebaseConfigured,
    isVerifiedGoogleAdmin,
} from './config';
import { fetchInviteByEmail } from '../admin/inviteRemote';
import { hasPortalInvite, hasPortalInviteForEmail } from '../admin/inviteStore';
import { ADMIN_ON_OWNER_PAGE_MESSAGE, getOwnerSignInBlockReason } from './ownerAccess';

export type SignedInUser = {
    email: string;
    displayName: string | null;
    role: UserRole;
};

type FirebaseAuth = import('firebase/auth').Auth;
type FirebaseUser = import('firebase/auth').User;

const resolveRole = async (
    firebaseUser: FirebaseUser | null
): Promise<UserRole | null> => {
    const email = firebaseUser?.email;
    if (isVerifiedGoogleAdmin(firebaseUser)) {
        return 'admin';
    }
    if (isAdminEmail(email)) {
        return null;
    }
    if (email && isFirebaseConfigured()) {
        const invite = await fetchInviteByEmail(email);
        return invite && hasPortalInvite(invite) ? 'owner' : null;
    }
    if (email && hasPortalInviteForEmail(email)) {
        return 'owner';
    }
    return null;
};

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let authPromise: Promise<FirebaseAuth | null> | null = null;

const loadAuth = async (): Promise<FirebaseAuth | null> => {
    if (!isFirebaseConfigured()) {
        return null;
    }

    if (!authPromise) {
        authPromise = (async () => {
            try {
                const { getApp, getApps, initializeApp } = await import('firebase/app');
                const { getAuth } = await import('firebase/auth');
                const app = getApps().length > 0
                    ? getApp()
                    : initializeApp(firebaseConfig);
                return getAuth(app);
            } catch (error) {
                console.error('Firebase failed to initialize', error);
                return null;
            }
        })();
    }

    return authPromise;
};

export const subscribeToAdminAuth = async (
    onUser: (user: SignedInUser | null) => void
): Promise<() => void> => {
    const auth = await loadAuth();
    if (!auth) {
        onUser(null);
        return () => undefined;
    }

    const { onAuthStateChanged, signOut } = await import('firebase/auth');
    return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
        const role = await resolveRole(firebaseUser);
        if (!firebaseUser?.email || !role) {
            if (firebaseUser) {
                await signOut(auth);
            }
            onUser(null);
            return;
        }

        onUser({
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            role,
        });
    });
};

export const signInAdminWithGoogle = async (): Promise<SignedInUser> => {
    const auth = await loadAuth();
    if (!auth) {
        throw new Error('Firebase is not configured yet.');
    }

    const { GoogleAuthProvider, signInWithPopup, signOut } = await import('firebase/auth');
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    if (!isVerifiedGoogleAdmin(result.user)) {
        await signOut(auth);
        throw new Error(
            isAdminEmail(result.user.email)
                ? ADMIN_GOOGLE_REQUIRED_MESSAGE
                : 'This Google account is not an admin.'
        );
    }

    return {
        email: result.user.email as string,
        displayName: result.user.displayName,
        role: 'admin',
    };
};

export const signInOwnerWithGoogle = async (): Promise<SignedInUser> => {
    const auth = await loadAuth();
    if (!auth) {
        throw new Error('Firebase is not configured yet.');
    }

    const { GoogleAuthProvider, signInWithPopup, signOut } = await import('firebase/auth');
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    const email = result.user.email;

    const blockReason = await getOwnerSignInBlockReason(email);
    if (blockReason || !email) {
        await signOut(auth);
        throw new Error(blockReason || (await getOwnerSignInBlockReason(null)) as string);
    }

    return { email, displayName: result.user.displayName, role: 'owner' };
};

export const signInOwnerWithPassword = async (
    email: string,
    password: string
): Promise<SignedInUser> => {
    const auth = await loadAuth();
    if (!auth) {
        throw new Error('Firebase is not configured yet.');
    }

    if (isAdminEmail(email)) {
        throw new Error(ADMIN_ON_OWNER_PAGE_MESSAGE);
    }

    const { signInWithEmailAndPassword, signOut } = await import('firebase/auth');
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    const signedInEmail = result.user.email;
    const role = await resolveRole(result.user);
    if (!signedInEmail || role !== 'owner') {
        await signOut(auth);
        throw new Error((await getOwnerSignInBlockReason(signedInEmail)) || 'This account cannot use the owner portal.');
    }

    return { email: signedInEmail, displayName: result.user.displayName, role: 'owner' };
};

export const getFirebaseIdToken = async (): Promise<string | null> => {
    const auth = await loadAuth();
    if (!auth?.currentUser) {
        return null;
    }
    return auth.currentUser.getIdToken();
};

export const signOutFirebase = async (): Promise<void> => {
    const auth = await loadAuth();
    if (!auth) {
        return;
    }

    const { signOut } = await import('firebase/auth');
    await signOut(auth);
};
